import React, { useEffect, useRef, useState } from "react";
import { BriefcaseBusiness, Check, GraduationCap, Layers3, LockKeyhole, ShieldCheck } from "lucide-react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Cookies from "universal-cookie";
import { apiFetch } from "../commons/axiosReq";
import "./Checkout.css";
import { FEATURES } from "../config/features";

const scopeInfo = {
  Study: { title: "مسیر تحصیلی", description: "دانشگاه‌ها، استادها، قالب‌های مکاتبه و مدیریت اپلای", icon: GraduationCap },
  Job: { title: "مسیر شغلی", description: "فرصت‌های شغلی، کارفرماها، رزومه و پیگیری درخواست", icon: BriefcaseBusiness },
  Both: { title: "بستهٔ کامل تحصیلی + شغلی", description: "دسترسی هم‌زمان به هر دو مسیر با ۱۵٪ تخفیف ترکیبی", icon: Layers3 },
};
const format = (value) => new Intl.NumberFormat("fa-IR").format(value);

const Checkout = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const requestedScope = searchParams.get("scope");
  const scope = scopeInfo[requestedScope] && (FEATURES.jobApplications || requestedScope === "Study") ? requestedScope : "Study";
  const planId = Number(searchParams.get("planId")) || 0;
  const [plan, setPlan] = useState(location.state?.plan || null);
  const [loading, setLoading] = useState(!plan);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(true);
  const paymentPending = useRef(false);
  const token = new Cookies().get("token");

  useEffect(() => {
    let active = true;
    setLoading(true);
    apiFetch("plan/GetHomePlans", "get").then((result) => {
      if (!active) return;
      const found = Array.isArray(result.data) ? result.data.find((item) => Number(item.planId) === planId) : null;
      setPlan(found);
      if (!found) setError("اطلاعات پلن دریافت نشد. لطفاً صفحه را تازه‌سازی کنید.");
      setLoading(false);
    });
    return () => { active = false; };
  }, [planId]);

  useEffect(() => {
    let active = true;
    setQuote(null);
    setQuoteLoading(true);
    setError("");
    apiFetch(`payment/quote?planId=${planId}&scope=${scope}`).then((result) => {
      if (!active) return;
      if (result.ok) setQuote(result.data);
      else setError(result.error || "دریافت مبلغ پرداخت ممکن نشد.");
      setQuoteLoading(false);
    });
    return () => { active = false; };
  }, [planId, scope]);
  const pricing = { total: quote?.amountToman, unit: "تومان" };
  const currentPath = `/checkout?planId=${planId}&scope=${scope}`;
  const ScopeIcon = scopeInfo[scope].icon;
  const recipientSummary = scope === "Study"
    ? `${format(plan?.teacherNumber || 0)} استاد و مخاطب دانشگاهی در هر برنامه`
    : scope === "Job"
      ? `${format(plan?.teacherNumber || 0)} موقعیت شغلی دارای ایمیل تماس در هر برنامه`
      : `${format(plan?.teacherNumber || 0)} مخاطب در هر مسیر`;

  const pay = async () => {
    if (!token) {
      navigate(`/SignUp?redirect=${encodeURIComponent(currentPath)}`);
      return;
    }
    if (!planId) {
      setError("اطلاعات این پلن هنوز از سرور دریافت نشده است. لطفاً از صفحهٔ پلن‌ها دوباره انتخاب کنید.");
      return;
    }
    if (paymentPending.current || !quote) return;
    paymentPending.current = true;
    setSubmitting(true);
    setError("");
    try {
      const result = await apiFetch("payment/orders", "post", { planId, scope, expectedAmountToman: quote.amountToman });
      if (!result.ok) throw new Error(result.error || "ایجاد پرداخت انجام نشد.");
      const url = new URL(result.data.paymentUrl);
      if (url.protocol !== "https:" || !["payment.zarinpal.com", "sandbox.zarinpal.com"].includes(url.hostname)
        || url.port || url.username || url.password || !/^\/pg\/StartPay\/[AS][a-zA-Z0-9]+$/.test(url.pathname)) {
        throw new Error("آدرس درگاه پرداخت معتبر نیست.");
      }
      window.location.assign(url.href);
    } catch (err) {
      setError(err.message || "اتصال به درگاه ممکن نشد. دوباره تلاش کنید.");
      paymentPending.current = false;
      setSubmitting(false);
    }
  };

  return <main className="checkout-page" dir="rtl"><div className="checkout-shell">
    <header className="checkout-head"><div><span>تکمیل انتخاب پلن</span><h1>جمع‌بندی و پرداخت اشتراک</h1><p>جزئیات را بررسی کنید؛ اشتراک پس از پرداخت موفق فعال می‌شود.</p></div><Link to="/#pricing">تغییر پلن</Link></header>
    <div className="checkout-grid"><section className="checkout-main-card">
      <div className="checkout-path"><span><ScopeIcon size={26} /></span><div><small>نوع اشتراک</small><h2>{scopeInfo[scope].title}</h2><p>{scopeInfo[scope].description}</p></div>{scope === "Both" && <b>۱۵٪ تخفیف ترکیبی</b>}</div>
      <div className="checkout-plan"><div><small>پلن انتخابی</small><h3>{loading ? "در حال دریافت..." : plan?.title || "پلن انتخاب نشده"}</h3><p>{plan?.description}</p></div><span>۳۰ روز اعتبار</span></div>
      <ul className="checkout-features"><li><Check size={17} /> ارسال به {recipientSummary}</li><li><Check size={17} /> فرصت‌های منتخب از {format(plan?.countryNumber || 0)} کشور</li><li><Check size={17} /> ارسال و پیگیری خودکار درخواست‌ها</li>{scope === "Both" && <li><Check size={17} /> دسترسی کامل به ابزارهای هر دو مسیر</li>}</ul>
      <div className="checkout-notice"><ShieldCheck size={20} /><div><strong>پرداخت امن با زرین‌پال</strong><p>برای پرداخت به درگاه بانکی منتقل می‌شوید. پس از تأیید پرداخت، اشتراک روی حساب شما فعال خواهد شد.</p></div></div>
    </section><aside className="checkout-summary"><h2>خلاصه سفارش</h2><dl><div><dt>پلن</dt><dd>{plan?.title || "—"}</dd></div><div><dt>مسیر</dt><dd>{scopeInfo[scope].title}</dd></div>{scope === "Both" && <div className="is-discount"><dt>تخفیف ترکیبی</dt><dd>۱۵٪ (اعمال شده)</dd></div>}<div className="checkout-total"><dt>مبلغ نهایی</dt><dd>{pricing.total ? <><strong>{format(pricing.total)}</strong><span>{pricing.unit}</span></> : quoteLoading ? "در حال دریافت..." : "فعلاً در دسترس نیست"}</dd></div></dl>
      {quote?.sandbox && <p className="checkout-error">پرداخت آزمایشی است و اشتراک واقعی فعال نمی‌کند.</p>}
      {error && <p className="checkout-error" role="alert">{error}</p>}
      <button type="button" onClick={pay} disabled={submitting || loading || quoteLoading || !quote || !plan}>{submitting ? "در حال انتقال به درگاه..." : token ? "پرداخت با زرین‌پال" : "ورود و ادامه"}<LockKeyhole size={17} /></button>
      {!token && <p className="checkout-auth-hint">حساب دارید؟ <Link to={`/SignIn?redirect=${encodeURIComponent(currentPath)}`}>وارد شوید</Link></p>}
      <small className="checkout-safe">اطلاعات کارت فقط در درگاه بانکی وارد می‌شود</small>
    </aside></div>
  </div></main>;
};

export default Checkout;
